/**
 * cascadeApproval.js
 * Pure, framework-free helpers for the workflow-level cascade approval modal (HU-05, S16 — jupiter
 * part of the E2E contract; sun M5/M5-correction already build the atomic save behind the existing
 * `PUT /workflow/{createNewVersion}/{propagate}/{indexValueName}` endpoint — no dedicated cascade
 * endpoint, same deviation already registered for D4/HU-04). Derives, from the already-loaded
 * workflow tree, every subworkflow/job/job-featureGroup whose indexValue is not APPROVED yet, and
 * applies APPROVED to all of them in a payload-ready, immutable way — same extraction pattern as
 * `bomTree.js`/`bomImportMapping.js`.
 *
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Workflow} Workflow Workflow object
 * @typedef {import('../../generic/utilities/objectStructures/globalStructures').IdName} IdName IdName object
 */

import { FEATURE_GROUP_STATES } from './feature/featureGroupRevisionControl'

const CASCADE_LEVEL = {
  SUBWORKFLOW: 'SUBWORKFLOW',
  JOB: 'JOB',
  FEATURE_GROUP: 'FEATURE_GROUP'
}

/** i18n label key per cascade level, shared by every cascade confirmation modal. */
const CASCADE_LEVEL_LABEL = {
  [CASCADE_LEVEL.SUBWORKFLOW]: 'Subworkflow',
  [CASCADE_LEVEL.JOB]: 'Job',
  [CASCADE_LEVEL.FEATURE_GROUP]: 'Feature Group'
}

/**
 * What the cascade does to ONE feature group in CONTROLLED mode (story 186, RN49). The cascade never blocks (fix: no
 * index restriction on feature groups): a group below the target is taken to it, whatever its persisted state.
 * - target DRAFT: nothing changes (H1, G2: going back to DRAFT is a manual choice);
 * - already at or above the target: untouched ('KEEP');
 * - otherwise: 'CHANGE'.
 *
 * @param {{ featureGroup: object, targetIndexValueName: string }} input
 * @returns {'KEEP'|'CHANGE'}
 */
const controlledFeatureGroupAction = ({ featureGroup, targetIndexValueName }) => {
  if (targetIndexValueName === 'DRAFT') {
    return 'KEEP'
  }
  const rank = (name) => FEATURE_GROUP_STATES.indexOf(name)
  return rank(featureGroup?.indexValue?.name) >= rank(targetIndexValueName) ? 'KEEP' : 'CHANGE'
}

/**
 * @typedef {Object} CascadePendingItem
 * @property {string} level One of `CASCADE_LEVEL`
 * @property {string} id
 * @property {string} code
 * @property {string} name
 * @property {string} indexValueName Current indexValue name (differing from the target)
 * @property {string} jobCode Code of the owning job (FEATURE_GROUP items; '' otherwise)
 * @property {string} jobName Name of the owning job (FEATURE_GROUP items; '' otherwise)
 * @property {string} taskName Name of the owning taskReference (FEATURE_GROUP items; '' otherwise)
 */

/**
 * S16 (RN05.1/RN05.2, enmienda 4 GATE 2 + enmienda PO 23/07/2026 — cascade generalized to any
 * target indexValue, not only APPROVED): recursively collects every subworkflow, job and
 * job-featureGroup of the given already-loaded workflow whose indexValue differs from the target,
 * each tagged with its hierarchy level (workflow -> subworkflow -> job -> featureGroup). The root
 * workflow itself is never included: it is the target of the action, not one of the items listed
 * by the modal.
 * @param {Workflow} workflow
 * @param {string} [targetIndexValueName] Target indexValue name (defaults to APPROVED)
 * @returns {CascadePendingItem[]}
 */
const walkCascade = (workflow, targetIndexValueName, { controlled = false } = {}) => {
  const pending = []
  const matchesTarget = (indexValue) => indexValue?.name === targetIndexValueName

  // Enmienda PO 23/07/2026: featureGroup rows identify the job and the task they belong to.
  const visitFeatureGroups = (taskReferences, jobNode) => {
    (taskReferences || []).forEach((taskReference) => {
      (taskReference.featureGroups || []).forEach((featureGroup) => {
        if (matchesTarget(featureGroup.indexValue)) {
          return
        }
        // Story 186 (CONTROLLED, RN49): a group already at or above the target is left as it is.
        if (controlled && controlledFeatureGroupAction({ featureGroup, targetIndexValueName }) === 'KEEP') {
          return
        }
        pending.push({
          level: CASCADE_LEVEL.FEATURE_GROUP,
          id: featureGroup.id || '',
          code: featureGroup.code || '',
          name: featureGroup.name || '',
          indexValueName: featureGroup.indexValue?.name || '',
          jobCode: jobNode?.code || '',
          jobName: jobNode?.name || '',
          taskName: taskReference.name || ''
        })
      })
    })
  }

  const visitNodes = (nodes) => {
    (nodes || []).forEach((node) => {
      if (node.nodeType === 'JOB') {
        if (!matchesTarget(node.jobReference?.indexValue)) {
          pending.push({
            level: CASCADE_LEVEL.JOB,
            id: node.id || '',
            code: node.code || '',
            name: node.name || '',
            indexValueName: node.jobReference?.indexValue?.name || '',
            jobCode: '',
            jobName: '',
            taskName: ''
          })
        }
        visitFeatureGroups(node.jobReference?.taskReferences, node)
        return
      }
      if (node.nodeType === 'WORKFLOW') {
        if (!matchesTarget(node.subWorkflow?.indexValue)) {
          pending.push({
            level: CASCADE_LEVEL.SUBWORKFLOW,
            id: node.id || '',
            code: node.code || '',
            name: node.name || '',
            indexValueName: node.subWorkflow?.indexValue?.name || '',
            jobCode: '',
            jobName: '',
            taskName: ''
          })
        }
        visitNodes(node.subWorkflow?.nodes)
      }
    })
  }

  visitNodes(workflow?.nodes)

  return pending
}

/** @see walkCascade; `policy` is `{ controlled }` (story 186), FREE mode when omitted. */
const collectCascadePendingItems = (workflow, targetIndexValueName = 'APPROVED', policy = {}) =>
  walkCascade(workflow, targetIndexValueName, policy)

/**
 * S16/S17 (parte jupiter) + enmienda PO 23/07/2026 (cascade generalized to any target): returns a
 * new workflow object with every differing subworkflow/job/featureGroup indexValue set to the
 * target, ready to travel inside the same save payload as the root — the atomicity of the whole
 * cascade is guaranteed server-side by the transaction that already wraps the workflow save (sun
 * M5-correction), not by this function. Never mutates the input; items already at the target are
 * left untouched (referential no-op).
 * @param {Workflow} workflow
 * @param {IdName[]} indexValues Index values allowed for the current user (indexValues/available)
 * @param {string} [targetIndexValueName] Target indexValue name (defaults to APPROVED)
 * @param {{ controlled?: boolean }} [policy] Story 186: CONTROLLED mode
 * @returns {Workflow}
 */
const applyCascadeApproval = (workflow, indexValues, targetIndexValueName = 'APPROVED', { controlled = false } = {}) => {
  const targetIndexValue = (indexValues || []).find((index) => index.name === targetIndexValueName)
  if (!targetIndexValue) {
    return workflow
  }
  const matchesTarget = (indexValue) => indexValue?.name === targetIndexValueName

  // Structures that were not loaded yet (undefined/null nodes, taskReferences or featureGroups)
  // are preserved AS-IS, never materialized as []: e.g. `handleSubWorkflowDoubleClick` decides
  // whether to fetch the full subworkflow (with its workflowPositions) precisely by whether
  // `subWorkflow.nodes` exists — an empty array introduced here would skip that fetch and crash
  // the graph render downstream.
  const cascadeFeatureGroups = (taskReferences) => {
    if (!taskReferences) {
      return taskReferences
    }
    return taskReferences.map((taskReference) => ({
      ...taskReference,
      featureGroups: taskReference.featureGroups
        ? taskReference.featureGroups.map((featureGroup) => {
          // Story 186 (CONTROLLED, RN49): a group already at or above the target stays as it is.
          if (matchesTarget(featureGroup.indexValue) || (controlled && controlledFeatureGroupAction({ featureGroup, targetIndexValueName }) !== 'CHANGE')) {
            return featureGroup
          }
          return { ...featureGroup, indexValue: targetIndexValue }
        })
        : taskReference.featureGroups
    }))
  }

  const cascadeNodes = (nodes) => {
    if (!nodes) {
      return nodes
    }
    return nodes.map((node) => {
      if (node.nodeType === 'JOB') {
        return {
          ...node,
          jobReference: {
            ...node.jobReference,
            indexValue: matchesTarget(node.jobReference?.indexValue) ? node.jobReference.indexValue : targetIndexValue,
            taskReferences: cascadeFeatureGroups(node.jobReference?.taskReferences)
          }
        }
      }
      if (node.nodeType === 'WORKFLOW') {
        return {
          ...node,
          subWorkflow: {
            ...node.subWorkflow,
            indexValue: matchesTarget(node.subWorkflow?.indexValue) ? node.subWorkflow.indexValue : targetIndexValue,
            nodes: cascadeNodes(node.subWorkflow?.nodes)
          }
        }
      }
      return node
    })
  }

  return {
    ...workflow,
    nodes: cascadeNodes(workflow?.nodes)
  }
}

/** The feature groups that travel in the save (they carry at least one feature), with the task they belong to. */
const travellingFeatureGroups = (jobReference) => (jobReference?.taskReferences || [])
  .flatMap((taskReference) => ((taskReference && taskReference.featureGroups) || [])
    .map((featureGroup) => ({ featureGroup, taskReference })))
  .filter(({ featureGroup }) => featureGroup && Array.isArray(featureGroup.features) && featureGroup.features.length > 0)

const cascadeItemOf = ({ featureGroup, taskReference }, jobNode) => ({
  level: CASCADE_LEVEL.FEATURE_GROUP,
  id: featureGroup.id || '',
  code: featureGroup.code || '',
  name: featureGroup.name || '',
  indexValueName: featureGroup.indexValue?.name || '',
  jobCode: jobNode?.code || '',
  jobName: jobNode?.name || '',
  taskName: taskReference.name || ''
})

/**
 * Enmienda PO 23/07/2026 — jobReference-level cascade: featureGroups (reachable through the
 * jobReference's taskReferences and carrying at least one feature, mirroring the save wire
 * mapping) whose indexValue differs from the target, listed for the confirmation modal.
 * Story 186 (CONTROLLED, RN49): only the groups that will really change are listed (see `controlledFeatureGroupAction`).
 * @param {Object} jobReference
 * @param {string} targetIndexValueName
 * @param {Object} [jobNode]
 * @param {{ controlled?: boolean }} [policy] CONTROLLED mode
 * @returns {Array<{id: *, name: string, indexValueName: string}>}
 */
const collectJobReferenceCascadeItems = (jobReference, targetIndexValueName, jobNode, { controlled = false } = {}) => {
  return travellingFeatureGroups(jobReference)
    .filter(({ featureGroup }) => controlled
      ? controlledFeatureGroupAction({ featureGroup, targetIndexValueName }) === 'CHANGE'
      : featureGroup.indexValue?.name !== targetIndexValueName)
    .map((entry) => cascadeItemOf(entry, jobNode))
}

/**
 * Enmienda PO 23/07/2026 — jobReference-level cascade: returns a new jobReference with its own
 * indexValue and every travelling featureGroup's indexValue set to the target. Never mutates the
 * input; featureGroups without features are left untouched (they never travel in the save).
 * Story 186 (CONTROLLED, RN49): a group already at or above the target is left as it is.
 * @param {Object} jobReference
 * @param {{id: *, name: string}} targetIndexValue
 * @param {{ controlled?: boolean }} [policy]
 * @returns {Object}
 */
const applyJobReferenceCascadeIndexValue = (jobReference, targetIndexValue, { controlled = false } = {}) => {
  return {
    ...jobReference,
    indexValue: targetIndexValue,
    taskReferences: (jobReference?.taskReferences || []).map((taskReference) => ({
      ...taskReference,
      featureGroups: (taskReference.featureGroups || []).map((featureGroup) => {
        const travels = Array.isArray(featureGroup?.features) && featureGroup.features.length > 0
        if (!travels || featureGroup.indexValue?.name === targetIndexValue.name) {
          return featureGroup
        }
        if (controlled && controlledFeatureGroupAction({ featureGroup, targetIndexValueName: targetIndexValue.name }) !== 'CHANGE') {
          return featureGroup
        }
        return { ...featureGroup, indexValue: targetIndexValue }
      })
    }))
  }
}

/**
 * Enmienda PO 23/07/2026 — the cascade must reach the nodes INSIDE every subworkflow, loaded or
 * not (and subworkflows can nest further subworkflows). Returns a new workflow tree where every
 * WORKFLOW node whose subworkflow content was not loaded yet gets it fetched through
 * `fetchWorkflowById`, recursively. Loaded subworkflows keep their in-memory (possibly edited)
 * object and are only recursed into. Run this BEFORE collecting/applying a cascade so the whole
 * tree is visible to it — otherwise opening a subworkflow after the cascade re-fetches it from
 * the backend and shows the persisted (previous) indexValues again.
 *
 * @param {Workflow} workflow
 * @param {function(*): Promise<Workflow>} fetchWorkflowById Fetches a full workflow by id
 * @returns {Promise<Workflow>}
 */
const hydrateWorkflowTree = async (workflow, fetchWorkflowById) => {
  const hydrateNodes = async (nodes) => {
    if (!nodes) {
      return nodes
    }
    return Promise.all(nodes.map(async (node) => {
      if (node.nodeType !== 'WORKFLOW') {
        return node
      }
      let subWorkflow = node.subWorkflow
      if (!subWorkflow?.nodes && subWorkflow?.id) {
        const loaded = await fetchWorkflowById(subWorkflow.id)
        subWorkflow = { ...subWorkflow, ...loaded }
      }
      return {
        ...node,
        subWorkflow: {
          ...subWorkflow,
          nodes: await hydrateNodes(subWorkflow?.nodes)
        }
      }
    }))
  }

  return {
    ...workflow,
    nodes: await hydrateNodes(workflow?.nodes)
  }
}

export {
  CASCADE_LEVEL,
  CASCADE_LEVEL_LABEL,
  hydrateWorkflowTree,
  collectCascadePendingItems,
  applyCascadeApproval,
  collectJobReferenceCascadeItems,
  applyJobReferenceCascadeIndexValue
}
